import { FC, useCallback } from 'react';
import { useFragment, useMutation } from 'react-relay';
import { Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import Form from 'components/Form';
import AccordionController from 'components/AccordionController';
import Section from 'components/Section';
import WhatToSeeEditMainFields from 'components/WhatToSeeEditMainFields';

import { SelectValue } from 'components/Select';
import { AutocompleteOption } from 'components/Autocomplete';
import { WhatToSeeEditMain_whatToSee$key } from '__generated__/WhatToSeeEditMain_whatToSee.graphql';
import { WhatToSeeEditMain_data$key } from '__generated__/WhatToSeeEditMain_data.graphql';
import { WhatToSeeEditMainMutation } from '__generated__/WhatToSeeEditMainMutation.graphql';
import { setEntityField, useErrorHandler } from 'lib';

export type FormFields = {
  name: string;
  description: string;
  poisDescription: string;
  country: AutocompleteOption | null;
  region: AutocompleteOption | null;
  city: AutocompleteOption | null;
  pois: readonly SelectValue[];
};

type Props = {
  data: WhatToSeeEditMain_data$key;
  whatToSee: WhatToSeeEditMain_whatToSee$key;
};

const WhatToSeeEditMain: FC<Props> = props => {
  const errorHandler = useErrorHandler();

  const { id, ...whatToSee } = useFragment(
    graphql`
      fragment WhatToSeeEditMain_whatToSee on WhatToSee {
        id
        name
        description
        poisDescription

        country {
          id
          name
        }

        region {
          id
          name
        }

        city {
          id
          name
        }

        pois {
          id
          name
        }
      }
    `,
    props.whatToSee
  );

  const data = useFragment(
    graphql`
      fragment WhatToSeeEditMain_data on Query {
        ...WhatToSeeEditMainFields_data
      }
    `,
    props.data
  );

  const [update, updating] = useMutation<WhatToSeeEditMainMutation>(graphql`
    mutation WhatToSeeEditMainMutation(
      $id: ID!
      $input: UpdateWhatToSeeInput!
    ) {
      updateWhatToSee(id: $id, input: $input) {
        ...EntitySidebar_entity
        ...WhatToSeeEditMain_whatToSee
      }
    }
  `);

  const initial = {
    name: whatToSee.name,
    description: whatToSee.description ?? '',
    poisDescription: whatToSee.poisDescription ?? '',
    country: whatToSee.country,
    region: whatToSee.region,
    city: whatToSee.city,
    pois: whatToSee.pois,
  };

  const handleSubmit = useCallback(
    (input: FormFields) => {
      update({
        variables: {
          id,
          input: {
            ...input,
            country: setEntityField(input.country),
            region: setEntityField(input.region),
            city: setEntityField(input.city),
            pois: input.pois.map(poi => setEntityField(poi)!),
          },
        },

        ...errorHandler({
          successMessage: 'Išsaugota',
        }),
      });
    },
    [errorHandler, id, update]
  );

  return (
    <Formik<FormFields> initialValues={initial} onSubmit={handleSubmit}>
      <Form loading={updating}>
        <AccordionController id="pageEdit">
          <Section id="main" title="Bendra informacija">
            <WhatToSeeEditMainFields data={data} />
          </Section>
        </AccordionController>
      </Form>
    </Formik>
  );
};

export default WhatToSeeEditMain;
